/**
 * Cross-app suggestions surfaced in the atlas detail drawer.
 *
 * The atlas is a directory of the practices and frameworks of this
 * work — each entity it names exists *as a practice* in another app
 * on the playground. When the visitor opens an entity here, the
 * drawer suggests the apps that would let them actually *do* the
 * thing the atlas describes.
 *
 * Slugs match the subdomain map in `apps/shell/src/data/apps.ts`.
 * `trance` is the editor at trance.hypneuronerd.ing and is allowed
 * as a target even though it's not in the shell's 12-app gallery.
 */

export interface CrossLink {
  /** App slug — matches subdomain. */
  slug: string;
  /** One short line explaining why this entity sends you to this app. */
  reason: string;
}

const url = (slug: string) => `https://${slug}.hypneuronerd.ing/`;

export function urlFor(slug: string): string {
  return url(slug);
}

/** Display name for an app slug (for the chip label). */
export const APP_LABELS: Record<string, string> = {
  'archetype-mirror': 'the archetype mirror',
  'card-sort': 'the card sort',
  'dose-calibrator': 'the dose calibrator',
  'ritual-forge': 'the ritual forge',
  'negotiation-letter': 'the negotiation letter',
  'aftercare-recipe': 'the aftercare recipe',
  candle: 'the candle',
  'daily-word': 'the daily word',
  'red-flag-green-flag': 'red flag, green flag',
  'negotiation-sim': 'the negotiation sim',
  'glossary-match': 'glossary match',
  trance: 'the trance engine',
};

/** Region cross-links keyed by region id. */
export const REGION_LINKS: Record<string, CrossLink[]> = {
  servant: [
    { slug: 'ritual-forge', reason: 'build a small ceremony of service to return to.' },
    { slug: 'aftercare-recipe', reason: 'the care that follows is part of the work.' },
  ],
  devotee: [
    { slug: 'ritual-forge', reason: 'devotion is built one repeated gesture at a time.' },
    { slug: 'candle', reason: 'ten minutes of stillness, watched by a flame.' },
  ],
  sensualist: [
    { slug: 'candle', reason: 'a single focal object trains attention to the body.' },
    { slug: 'archetype-mirror', reason: 'see where the sensualist sits in your shape.' },
  ],
  primal: [
    { slug: 'archetype-mirror', reason: 'check how strongly the primal pulls you.' },
    { slug: 'dose-calibrator', reason: 'size primal play to a life that holds it.' },
  ],
  brat: [
    { slug: 'archetype-mirror', reason: 'the brat lights up on its own constellation.' },
    { slug: 'glossary-match', reason: 'community vocabulary, learned without judgment.' },
  ],
  little: [
    { slug: 'archetype-mirror', reason: 'see whether little-space is part of your shape.' },
    { slug: 'daily-word', reason: 'one gentle quality to wear today.' },
  ],
};

/** Landmark cross-links keyed by landmark id. */
export const LANDMARK_LINKS: Record<string, CrossLink[]> = {
  'negotiation-library': [
    { slug: 'negotiation-letter', reason: 'draft what you want before you have to say it.' },
    { slug: 'negotiation-sim', reason: 'roleplay the conversation in low stakes first.' },
  ],
  'safeword-bell-tower': [
    { slug: 'negotiation-sim', reason: 'practice saying no, not-yet, and the safeword.' },
    { slug: 'red-flag-green-flag', reason: 'train the gut that knows when to ring it.' },
  ],
  'aftercare-springs': [
    { slug: 'aftercare-recipe', reason: 'a tailored plan for after — print it, bring it.' },
  ],
  'collar-cathedral': [
    { slug: 'ritual-forge', reason: 'compose the ceremony that marks the commitment.' },
    { slug: 'negotiation-letter', reason: 'write what the collar will and will not mean.' },
  ],
  'threshold-bell': [
    { slug: 'ritual-forge', reason: 'build the entry and exit thresholds you ring it at.' },
  ],
  'contract-scriptorium': [
    { slug: 'negotiation-letter', reason: 'this is the scriptorium, in app form.' },
    { slug: 'negotiation-sim', reason: 'rehearse a contract clause before you sign it.' },
  ],
  'rope-shrine': [
    { slug: 'ritual-forge', reason: 'build the small ceremony around the tying.' },
    { slug: 'dose-calibrator', reason: 'rope is a practice — calibrate it to your life.' },
  ],
  'munch-tavern': [
    { slug: 'glossary-match', reason: 'walk in fluent in the local dialect.' },
    { slug: 'card-sort', reason: 'know your yes / no / curious before the conversation.' },
  ],
  'consent-fountain': [
    { slug: 'red-flag-green-flag', reason: 'consent practice as fast-recall pattern training.' },
    { slug: 'negotiation-sim', reason: 'hear yourself ask, and hear yourself refuse.' },
  ],
  'drop-meadow': [
    { slug: 'aftercare-recipe', reason: 'a plan for the days after, when drop arrives.' },
    { slug: 'daily-word', reason: 'one small quality to hold while you wait it through.' },
  ],
  'subspace-grove': [
    { slug: 'candle', reason: 'subspace cannot be summoned — but stillness can be practiced.' },
    { slug: 'trance', reason: 'a longer guided descent, when you want depth.' },
  ],
  'vetting-watchtower': [
    { slug: 'red-flag-green-flag', reason: 'the watchtower in app form — flag-spotting practice.' },
    { slug: 'negotiation-sim', reason: 'hear how a refusal lands before you have to give one.' },
  ],
};

/** Route cross-links keyed by route id. */
export const ROUTE_LINKS: Record<string, CrossLink[]> = {
  'bedroom-path': [
    { slug: 'dose-calibrator', reason: 'this is the lowest dose — verify the fit.' },
    { slug: 'candle', reason: 'contained, single-session practice that suits this path.' },
  ],
  'relationship-road': [
    { slug: 'dose-calibrator', reason: 'a partnership shape — re-calibrate when life shifts.' },
    { slug: 'ritual-forge', reason: 'the small repeated rituals that carry it.' },
  ],
  'lifestyle-highway': [
    { slug: 'dose-calibrator', reason: 'heavier dose — make the trade-offs visible.' },
    { slug: 'ritual-forge', reason: 'lifestyle is built ritual by ritual.' },
  ],
  'tpe-way': [
    { slug: 'dose-calibrator', reason: 'the heaviest dose — calibrate with eyes wide open.' },
    { slug: 'negotiation-letter', reason: 'write the contract that holds the authority.' },
  ],
};
